import { Building2, ChevronDown, Check } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useCompanyStore } from '../../context/companyStore';

/**
 * Company filter dropdown for users with multi-company access
 * Only renders if user has access to multiple companies
 */
export default function CompanyFilter({ className = '' }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const {
    companies,
    selectedCompanyId,
    hasMultipleCompanies,
    setSelectedCompanyId,
    clearSelection,
    getCompanyName
  } = useCompanyStore();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if user doesn't have multiple companies
  if (!hasMultipleCompanies || companies.length <= 1) {
    return null;
  }

  const handleSelect = (companyId) => {
    if (companyId === null) {
      clearSelection();
    } else {
      setSelectedCompanyId(companyId);
    }
    setIsOpen(false);
  };

  const selectedLabel = selectedCompanyId
    ? getCompanyName(selectedCompanyId)
    : `All Companies (${companies.length})`;

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors text-sm font-medium text-neutral-700 dark:text-neutral-200"
      >
        <Building2 className="w-4 h-4 text-neutral-500 dark:text-neutral-400" />
        <span className="max-w-[200px] truncate">{selectedLabel}</span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-1 max-h-64 overflow-y-auto">
          {/* All Companies option */}
          <button
            onClick={() => handleSelect(null)}
            className={`flex items-center justify-between w-full px-4 py-2 text-sm text-left hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
              selectedCompanyId === null
                ? 'text-primary-600 dark:text-primary-400 font-medium'
                : 'text-neutral-700 dark:text-neutral-200'
            }`}
          >
            <span>All Companies ({companies.length})</span>
            {selectedCompanyId === null && (
              <Check className="w-4 h-4 text-primary-500" />
            )}
          </button>

          <hr className="my-1 border-neutral-200 dark:border-neutral-700" />

          {/* Individual company options */}
          {companies.map((company) => (
            <button
              key={company.id}
              onClick={() => handleSelect(company.id)}
              className={`flex items-center justify-between w-full px-4 py-2 text-sm text-left hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                selectedCompanyId === company.id
                  ? 'text-primary-600 dark:text-primary-400 font-medium'
                  : 'text-neutral-700 dark:text-neutral-200'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate">{company.name}</span>
                {company.isPrimary && (
                  <span className="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300">
                    Primary
                  </span>
                )}
              </div>
              {selectedCompanyId === company.id && (
                <Check className="w-4 h-4 text-primary-500 flex-shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
